import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@mui/icons-material';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { paymentService } from '../../services/subscription/payment.service';
import { PaymentMethod } from '../../services/subscription/subscription.service';
import ErrorDisplay from '../common/feedback/ErrorDisplay';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';

// Get Stripe publishable key from environment
const STRIPE_PUBLISHABLE_KEY = process.env['REACT_APP_STRIPE_PUBLISHABLE_KEY'] || 'pk_test_placeholder';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface PaymentMethodsManagerProps {
  organizationId: string;
}

interface AddPaymentMethodFormProps {
  organizationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * Form component for adding a new payment method using Stripe Elements
 */
const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = ({
  organizationId,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setAsDefault, setSetAsDefault] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Create payment method with Stripe
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (!paymentMethod) {
        throw new Error('Failed to create payment method');
      }

      // Add payment method to organization via API
      await paymentService.addPaymentMethod(
        organizationId,
        paymentMethod.id,
        setAsDefault
      );

      onSuccess();
    } catch (err: any) {
      console.error('Error adding payment method:', err);
      setError(err.message || 'Failed to add payment method');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your card details to add a new payment method
        </Typography>

        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 2
          }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <input
            type="checkbox"
            id="set-as-default"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
          />
          <label htmlFor="set-as-default">
            <Typography variant="body2">
              Set as default payment method
            </Typography>
          </label>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {loading ? 'Adding...' : 'Add Payment Method'}
        </Button>
      </DialogActions>
    </form>
  );
};

/**
 * Payment Methods Manager Component
 * Displays and manages payment methods for an organization
 */
const PaymentMethodsManager: React.FC<PaymentMethodsManagerProps> = ({ organizationId }) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load payment methods
  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      const methods = await paymentService.listPaymentMethods(organizationId);
      setPaymentMethods(methods);
    } catch (err: any) {
      console.error('Error loading payment methods:', err);
      setError(err.message || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      loadPaymentMethods();
    }
  }, [organizationId]);

  // Handle set default
  const handleSetDefault = async (paymentMethod: PaymentMethod) => {
    if (paymentMethod.isDefault) return;

    setActionLoading(true);
    try {
      await paymentService.setDefaultPaymentMethod(paymentMethod.id);
      await loadPaymentMethods();
    } catch (err: any) {
      console.error('Error setting default payment method:', err);
      setError(err.message || 'Failed to set default payment method');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete
  const handleDeleteClick = (paymentMethod: PaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPaymentMethod) return;

    setActionLoading(true);
    try {
      await paymentService.removePaymentMethod(selectedPaymentMethod.id);
      setDeleteConfirmOpen(false);
      setSelectedPaymentMethod(null);
      await loadPaymentMethods();
    } catch (err: any) {
      console.error('Error deleting payment method:', err);
      setError(err.message || 'Failed to delete payment method');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle add success
  const handleAddSuccess = async () => {
    setAddDialogOpen(false);
    await loadPaymentMethods();
  };

  // Get card brand icon color
  const getCardBrandColor = (brand?: string): string => {
    const brandColors: { [key: string]: string } = {
      visa: '#1A1F71',
      mastercard: '#EB001B',
      amex: '#006FCF',
      discover: '#FF6000',
    };
    return brandColors[brand?.toLowerCase() || ''] || '#666';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Payment Methods
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={actionLoading}
        >
          Add Payment Method
        </Button>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Payment methods list */}
      {paymentMethods.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CreditCardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No payment methods added
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a payment method to manage subscriptions and billing
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Your First Payment Method
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {paymentMethods.map((pm) => (
            <Grid item xs={12} sm={6} md={4} key={pm.id}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    {/* Card brand and default indicator */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={pm.brand?.toUpperCase() || 'CARD'}
                        size="small"
                        sx={{
                          bgcolor: getCardBrandColor(pm.brand),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                      {pm.isDefault && (
                        <Chip
                          icon={<StarIcon />}
                          label="Default"
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>

                    {/* Card number */}
                    <Typography variant="h6" sx={{ letterSpacing: 2 }}>
                      •••• •••• •••• {pm.last4}
                    </Typography>

                    {/* Expiry date */}
                    <Typography variant="body2" color="text.secondary">
                      Expires {pm.expiryMonth}/{pm.expiryYear}
                    </Typography>
                  </Stack>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  {/* Set default button */}
                  {!pm.isDefault && (
                    <Button
                      size="small"
                      startIcon={<StarBorderIcon />}
                      onClick={() => handleSetDefault(pm)}
                      disabled={actionLoading}
                    >
                      Set Default
                    </Button>
                  )}
                  {pm.isDefault && <Box />}

                  {/* Delete button */}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(pm)}
                    disabled={actionLoading || pm.isDefault}
                    title={pm.isDefault ? 'Cannot delete default payment method' : 'Delete payment method'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add payment method dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => !actionLoading && setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Payment Method</DialogTitle>
        <Elements stripe={stripePromise}>
          <AddPaymentMethodForm
            organizationId={organizationId}
            onSuccess={handleAddSuccess}
            onCancel={() => setAddDialogOpen(false)}
          />
        </Elements>
      </Dialog>

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete Payment Method"
        content={`Are you sure you want to delete this payment method ending in ${selectedPaymentMethod?.last4}?`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setSelectedPaymentMethod(null);
        }}
        isLoading={actionLoading}
      />
    </Box>
  );
};

export default PaymentMethodsManager;
